// Real call driver for the power dialer: ONE Twilio Device for the whole session (gap 3),
// created on the first dial and destroyed when the session ends. Separate from the
// click-to-call Device in DialerSliderCard, which mounts only while that floater is open
// and stays exactly as it was.
import { Device } from "@twilio/voice-sdk";
import { cryptoKey, SERVER_URL } from "Static/constant";
import DialerServiceData from "Stores/DialerServiceStore";
import userDetail from "Stores/userDetail";
import { makeApi } from "Utils/apiURL";
import Utils from "Utils/utils";

const CryptoJS = require("crypto-js");

let device = null;
const calls = {}; // callSid -> { call, acceptedAt, disconnectedAt }

const decrypt = token => {
  const key = CryptoJS.enc.Base64.parse(cryptoKey);
  return CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Base64.parse(token) }, key, {
    mode: CryptoJS.mode.ECB
  }).toString(CryptoJS.enc.Utf8);
};

const freshToken = async () => {
  await DialerServiceData.getDailerToken();
  const token = DialerServiceData.dialerTokenObj?.capabilityToken;
  if (!token) throw new Error("No dialer token");
  return decrypt(token);
};

const ensureDevice = async () => {
  if (device && device.state === "registered") return device;
  if (!device) {
    device = new Device(await freshToken(), { codecPreferences: ["opus", "pcmu"], logLevel: "warn" });
    device.on("tokenWillExpire", async () => {
      try {
        device.updateToken(await freshToken());
      } catch (e) {}
    });
  }
  if (device.state !== "registered") await device.register();
  return device;
};

// Resolves with the parent CallSid once Twilio has it (ringing/accept), or rejects.
const waitForSid = call =>
  new Promise((resolve, reject) => {
    const done = () => call.parameters?.CallSid && resolve(call.parameters.CallSid);
    call.on("ringing", done);
    call.on("accept", done);
    call.on("error", reject);
    call.on("disconnect", () => (call.parameters?.CallSid ? done() : reject(new Error("Call ended"))));
    setTimeout(() => reject(new Error("No CallSid")), 20000);
  });

export const twilioDriver = {
  async dial(item = {}, ctx = {}) {
    const d = await ensureDevice();
    const callerId = DialerServiceData.dialerDefaultNo;
    if (!callerId || callerId === "purchaseNumber") throw new Error("No calling number");
    const params = {
      to_number: item.phone,
      caller_id: callerId,
      record: String(!!userDetail?.userFeatureFlag?.recordingEnabled),
      task_id: item.taskId || "",
      memberId: userDetail?.userInfo?.memberId || "",
      contactId: item.contactId || "",
      randomNo: String(Utils.generateRandomNumber()),
      dialerSessionId: ctx.dialerSessionId || "",
      ...(ctx.voicemailParams || {})
    };
    const call = await d.connect({ params });
    const entry = { call, acceptedAt: 0, disconnectedAt: 0, detectVoicemail: params.detectVoicemail === "true" };
    call.on("accept", () => (entry.acceptedAt = Date.now()));
    call.on("disconnect", () => (entry.disconnectedAt = Date.now()));
    call.on("error", e => (entry.errorCode = String(e?.code || "")));
    const callSid = await waitForSid(call);
    calls[callSid] = entry;
    return { callSid };
  },

  hangup(callSid) {
    try {
      const c = calls[callSid]?.call;
      if (c) c.disconnect();
    } catch (e) {}
  },

  mute(callSid, muted) {
    try {
      const c = calls[callSid]?.call;
      if (c) c.mute(!!muted);
    } catch (e) {}
  },

  // Server call-state (AMD + final dial result) merged with what the browser leg knows:
  // with Detect voicemail off the server never learns of a human answer, so accept = connected.
  async callState(callSid) {
    const entry = calls[callSid] || {};
    let s = {};
    try {
      s = (await makeApi({ url: `${SERVER_URL}/ds/twilio/call-state/${callSid}` }))?.data || {};
    } catch (e) {}
    const answeredBy = s.answeredBy || "unknown";
    if (s.status === "completed") return s;
    if (entry.acceptedAt && answeredBy === "unknown") {
      return { ...s, status: "in-progress", answeredBy: !entry.detectVoicemail || Date.now() - entry.acceptedAt > 15000 ? "human" : "unknown" };
    }
    // Hung up before the after-call webhook landed: give it a few seconds, then call it a no-answer.
    if (entry.disconnectedAt && Date.now() - entry.disconnectedAt > 3000) {
      if (entry.errorCode && !entry.acceptedAt) return { status: "completed", answeredBy, dialResult: "failed", errorCode: entry.errorCode };
      return { status: "completed", answeredBy, dialResult: entry.acceptedAt ? "completed" : "no-answer" };
    }
    return { ...s, status: s.status || "ringing", answeredBy, dialResult: s.dialResult || "" };
  },

  destroy() {
    Object.keys(calls).forEach(sid => delete calls[sid]);
    if (device) {
      try {
        device.destroy();
      } catch (e) {}
      device = null;
    }
  }
};
